/**
 * NotificationAggregator - Unified notification service
 * 
 * Aggregates notifications from all realtime managers (support tickets, licenses, chat, dashboard)
 * and provides a centralized interface for the UI to consume notifications.
 * 
 * Features:
 * - Subscribes to all notification sources
 * - Transforms raw events into unified Notification objects
 * - Persists notifications to IndexedDB
 * - Manages read/unread state
 * - Provides filtering and prioritization
 * - Handles notification dismissal
 */

import notificationStore, { type StoredNotification } from './notificationStore'
import supportTicketManager, { type SupportTicketListener } from './realtimeSupportTickets'
import licenseManager, { type LicenseListener } from './realtimeLicenses'
import realtimeLogger from './realtimeLogger'

export interface Notification {
  id: string
  type: 'support-ticket' | 'license' | 'chat' | 'dashboard' | 'system'
  priority: 'low' | 'normal' | 'high' | 'critical'
  title: string
  message: string
  timestamp: string
  read: boolean
  actionUrl?: string
  metadata: Record<string, unknown>
}

export type NotificationListener = (notifications: Notification[]) => void

/**
 * Batching configuration for different notification types
 */
interface BatchConfig {
  enabled: boolean
  debounceMs: number
  maxBatchSize?: number
}

/**
 * Pending notification in batch queue
 */
interface PendingNotification {
  notification: StoredNotification
  timestamp: number
}

class NotificationAggregator {
  private userId: number | null = null
  private businessId: number | null = null
  private listeners = new Set<NotificationListener>()
  private unsubscribeFunctions: Array<() => void> = []
  private isInitialized = false

  // Batching state
  private batchQueues: Map<string, PendingNotification[]> = new Map()
  private batchTimers: Map<string, NodeJS.Timeout> = new Map()
  private lastDashboardUpdate: number = 0
  
  // Batching configuration per notification type
  private batchConfig: Record<string, BatchConfig> = {
    dashboard: {
      enabled: true,
      debounceMs: 5000, // Max 1 per 5 seconds per requirements
    },
    'activity-feed': {
      enabled: true,
      debounceMs: 2000, // Batch activity feed items
      maxBatchSize: 10,
    },
    'support-ticket': {
      enabled: true,
      debounceMs: 1000, // Combine similar ticket updates
      maxBatchSize: 5,
    },
    license: {
      enabled: false, // Don't batch critical license events
      debounceMs: 0,
    },
    chat: {
      enabled: false, // Don't batch chat messages
      debounceMs: 0,
    },
  }

  /**
   * Initialize the notification aggregator
   * Sets up subscriptions to all notification sources
   */
  async initialize(userId: number, businessId: number): Promise<void> {
    if (this.isInitialized) {
      console.warn('[NotificationAggregator] Already initialized')
      return
    }

    this.userId = userId
    this.businessId = businessId

    console.log('[NotificationAggregator] Initializing for user', userId, 'business', businessId)

    // Clean up old notifications on initialization
    await notificationStore.cleanup()

    // Subscribe to support ticket notifications
    this.subscribeToSupportTickets()

    // Subscribe to license notifications
    this.subscribeToLicenses()

    // Subscribe to chat notifications
    this.subscribeToChat()

    this.isInitialized = true

    // Load and emit initial notifications
    await this.emitNotifications()
  }

  /**
   * Subscribe to support ticket events
   */
  private subscribeToSupportTickets(): void {
    if (!this.userId || !this.businessId) return

    console.log('[NotificationAggregator] Subscribing to support tickets for user:', this.userId, 'business:', this.businessId)

    const listener: SupportTicketListener = async (payload: Parameters<SupportTicketListener>[0]) => {
      console.log('[NotificationAggregator] Support ticket event received:', payload)
      realtimeLogger.logEventReceived(
        `support-ticket:${payload.support_ticket_id}`,
        payload.event,
        payload.data
      )

      const notification = this.transformSupportTicketEvent(payload)
      if (notification) {
        console.log('[NotificationAggregator] Transformed to notification:', notification)
        realtimeLogger.logNotificationReceived(notification.type, notification.priority, notification.id)
        await this.saveNotification(notification)
        await this.emitNotifications()
      } else {
        console.log('[NotificationAggregator] Event did not transform to notification')
      }
    }

    // Subscribe to user's tickets
    console.log('[NotificationAggregator] Subscribing to user channel: user:' + this.userId)
    const unsubUser = supportTicketManager.subscribeToUserTickets(this.userId, listener)
    this.unsubscribeFunctions.push(unsubUser)

    // Subscribe to business tickets
    console.log('[NotificationAggregator] Subscribing to business channel: business:' + this.businessId)
    const unsubBusiness = supportTicketManager.subscribeToBusinessTickets(this.businessId, listener)
    this.unsubscribeFunctions.push(unsubBusiness)
  }

  /**
   * Subscribe to license events
   */
  private subscribeToLicenses(): void {
    if (!this.userId || !this.businessId) return

    const listener: LicenseListener = async (payload: Parameters<LicenseListener>[0]) => {
      console.log('[NotificationAggregator] License event:', payload)
      realtimeLogger.logEventReceived(
        `license:${payload.business_license_id}`,
        payload.event,
        payload.data
      )

      const notification = this.transformLicenseEvent(payload)
      if (notification) {
        realtimeLogger.logNotificationReceived(notification.type, notification.priority, notification.id)
        await this.saveNotification(notification)
        await this.emitNotifications()
      }
    }

    // Subscribe to user's licenses
    const unsubUser = licenseManager.subscribeToUserLicenses(this.userId, listener)
    this.unsubscribeFunctions.push(unsubUser)

    // Subscribe to business licenses
    const unsubBusiness = licenseManager.subscribeToBusinessLicenses(this.businessId, listener)
    this.unsubscribeFunctions.push(unsubBusiness)
  }

  /**
   * Subscribe to chat message events
   */
  private subscribeToChat(): void {
    if (!this.userId) return

    // Note: Chat subscriptions are typically per-chat, not global
    // This is a placeholder for future implementation if needed
    // Individual chats should be subscribed to separately by the chat UI
  }

  /**
   * Transform dashboard event into notification
   * 
   * Priority levels:
   * - Low: Dashboard updates (per requirements)
   */
  private transformDashboardEvent(payload: {
    event: string
    business_id: number
    data: Record<string, unknown>
  }): StoredNotification | null {
    if (!this.userId || !this.businessId) return null

    const { event, business_id, data } = payload

    let title = ''
    let message = ''
    const priority: StoredNotification['priority'] = 'low' // Low priority per requirements
    const actionUrl = '/dashboard'

    switch (event) {
      case 'dashboard:update': {
        title = 'Dashboard Updated'
        message = 'Dashboard metrics have been updated'
        break
      }

      case 'dashboard:metrics-changed': {
        title = 'Metrics Updated'
        message = 'Your business metrics have changed'
        break
      }

      default:
        return null
    }

    return {
      id: `dashboard-${business_id}-${Date.now()}`,
      userId: this.userId,
      businessId: this.businessId,
      type: 'dashboard',
      priority,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false,
      dismissed: false,
      actionUrl,
      metadata: {
        business_id,
        event,
        ...data,
      },
      expiresAt: this.calculateExpiryDate(),
    }
  }

  /**
   * Transform activity feed event into notification
   * 
   * Priority levels:
   * - Low: Activity feed updates (per requirements)
   */
  private transformActivityFeedEvent(payload: {
    event: string
    business_id: number
    data: Record<string, unknown>
  }): StoredNotification | null {
    if (!this.userId || !this.businessId) return null

    const { event, business_id, data } = payload

    let title = ''
    let message = ''
    const priority: StoredNotification['priority'] = 'low' // Low priority per requirements
    const actionUrl = '/dashboard'

    switch (event) {
      case 'activity:new': {
        title = 'New Activity'
        message = data.description as string || 'New activity recorded'
        break
      }

      case 'activity:update': {
        title = 'Activity Updated'
        message = 'An activity has been updated'
        break
      }

      default:
        return null
    }

    return {
      id: `activity-${business_id}-${Date.now()}`,
      userId: this.userId,
      businessId: this.businessId,
      type: 'system',
      priority,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false,
      dismissed: false,
      actionUrl,
      metadata: {
        business_id,
        event,
        ...data,
      },
      expiresAt: this.calculateExpiryDate(),
    }
  }

  /**
   * Transform support ticket event into notification
   * 
   * Priority levels:
   * - High: Support ticket assigned
   * - Normal: Ticket status changes, created
   * - Low: Ticket closed
   */
  private transformSupportTicketEvent(payload: Parameters<SupportTicketListener>[0]): StoredNotification | null {
    if (!this.userId || !this.businessId) return null

    const { event, support_ticket_id, data } = payload

    let title = ''
    let message = ''
    let priority: StoredNotification['priority'] = 'normal'
    const actionUrl = `/support-tickets/${support_ticket_id}`

    switch (event) {
      case 'support-ticket:created': {
        title = 'New Support Ticket'
        message = `Support ticket #${support_ticket_id} has been created`
        priority = 'normal'
        break
      }

      case 'support-ticket:assigned': {
        title = 'Ticket Assigned'
        message = `You have been assigned to support ticket #${support_ticket_id}`
        priority = 'high' // High priority per requirements
        break
      }

      case 'support-ticket:status-changed': {
        title = 'Ticket Status Changed'
        message = `Support ticket #${support_ticket_id} status changed to ${data.status_slug || 'updated'}`
        priority = 'normal' // Normal priority per requirements
        break
      }

      case 'support-ticket:closed': {
        title = 'Ticket Closed'
        message = `Support ticket #${support_ticket_id} has been closed`
        priority = 'low'
        break
      }

      case 'support-ticket:update': {
        title = 'Ticket Updated'
        message = `Support ticket #${support_ticket_id} has been updated`
        priority = 'normal' // Normal priority per requirements
        break
      }

      default:
        return null
    }

    return {
      id: `support-ticket-${support_ticket_id}-${Date.now()}`,
      userId: this.userId,
      businessId: this.businessId,
      type: 'support-ticket',
      priority,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false,
      dismissed: false,
      actionUrl,
      metadata: {
        support_ticket_id,
        event,
        ...data,
      },
      expiresAt: this.calculateExpiryDate(),
    }
  }

  /**
   * Transform license event into notification
   * 
   * Priority levels:
   * - Critical: License revocation, payment failures
   * - High: License expiring in 1 day, capacity warnings
   * - Normal: License expiring in 3-7 days, renewed, assigned
   * - Low: License updates
   */
  private transformLicenseEvent(payload: Parameters<LicenseListener>[0]): StoredNotification | null {
    if (!this.userId || !this.businessId) return null

    const { event, business_license_id, data } = payload

    let title = ''
    let message = ''
    let priority: StoredNotification['priority'] = 'normal'
    let actionUrl = '/licenses'

    switch (event) {
      case 'license:assigned': {
        title = 'License Assigned'
        message = 'A license has been assigned to you'
        priority = 'normal'
        break
      }

      case 'license:revoked': {
        title = 'License Revoked'
        message = data.reason || 'Your license has been revoked'
        priority = 'critical'
        break
      }

      case 'license:expiring': {
        const daysUntilExpiry = data.days_until_expiry || 0
        title = 'License Expiring Soon'
        message = `Your license will expire in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`
        
        // Priority based on days until expiry:
        // - High: 1 day
        // - Normal: 3-7 days
        if (daysUntilExpiry <= 1) {
          priority = 'high'
        } else if (daysUntilExpiry >= 3 && daysUntilExpiry <= 7) {
          priority = 'normal'
        } else {
          priority = 'normal' // Default for other cases
        }
        break
      }

      case 'license:renewed': {
        title = 'License Renewed'
        message = 'Your license has been successfully renewed'
        priority = 'normal'
        break
      }

      case 'license:capacity-warning': {
        title = 'License Capacity Warning'
        message = `License capacity reached: ${data.capacity_info?.current || 0}/${data.capacity_info?.max || 0} ${data.capacity_info?.type || 'items'}`
        priority = 'high'
        break
      }

      case 'license:payment-failed': {
        title = 'License Payment Failed'
        message = data.failure_reason || 'License payment failed. Please update your payment method.'
        priority = 'critical' // Critical priority per requirements
        actionUrl = '/billing'
        break
      }

      case 'license:update': {
        title = 'License Updated'
        message = 'Your license has been updated'
        priority = 'low'
        break
      }

      default:
        return null
    }

    return {
      id: `license-${business_license_id}-${Date.now()}`,
      userId: this.userId,
      businessId: this.businessId,
      type: 'license',
      priority,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false,
      dismissed: false,
      actionUrl,
      metadata: {
        business_license_id,
        event,
        ...data,
      },
      expiresAt: this.calculateExpiryDate(),
    }
  }

  /**
   * Calculate expiry date for notification (30 days from now)
   */
  private calculateExpiryDate(): string {
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + 30)
    return expiryDate.toISOString()
  }

  /**
   * Get batch key for a notification
   * Used to group similar notifications together
   */
  private getBatchKey(notification: StoredNotification): string {
    const { type, metadata } = notification

    switch (type) {
      case 'dashboard':
        // All dashboard updates for same business batched together
        return `dashboard-${metadata.business_id}`

      case 'system':
        // Activity feed items batched together
        if (metadata.event === 'activity:new' || metadata.event === 'activity:update') {
          return `activity-feed-${metadata.business_id}`
        }
        return `system-${notification.id}`

      case 'support-ticket':
        // Batch updates for the same ticket
        return `support-ticket-${metadata.support_ticket_id}`

      case 'license':
        // Don't batch license notifications (critical)
        return `license-${notification.id}`

      case 'chat':
        // Don't batch chat messages
        return `chat-${notification.id}`

      default:
        return `${type}-${notification.id}`
    }
  }

  /**
   * Get batch configuration for a notification type
   */
  private getBatchConfig(notification: StoredNotification): BatchConfig {
    const { type, metadata } = notification

    // Special handling for activity feed
    if (type === 'system' && (metadata.event === 'activity:new' || metadata.event === 'activity:update')) {
      return this.batchConfig['activity-feed']
    }

    return this.batchConfig[type] || { enabled: false, debounceMs: 0 }
  }

  /**
   * Check if notification should be batched
   */
  private shouldBatch(notification: StoredNotification): boolean {
    const config = this.getBatchConfig(notification)
    
    // Don't batch critical notifications
    if (notification.priority === 'critical') {
      return false
    }

    return config.enabled
  }

  /**
   * Add notification to batch queue
   */
  private addToBatch(notification: StoredNotification): void {
    const batchKey = this.getBatchKey(notification)
    const config = this.getBatchConfig(notification)

    // Get or create queue for this batch key
    if (!this.batchQueues.has(batchKey)) {
      this.batchQueues.set(batchKey, [])
    }

    const queue = this.batchQueues.get(batchKey)!
    
    // Add to queue
    queue.push({
      notification,
      timestamp: Date.now(),
    })

    console.log(`[NotificationAggregator] Added to batch queue: ${batchKey}, queue size: ${queue.length}`)
    realtimeLogger.logNotificationBatched(batchKey, queue.length)

    // Check if we should flush immediately due to max batch size
    if (config.maxBatchSize && queue.length >= config.maxBatchSize) {
      console.log(`[NotificationAggregator] Max batch size reached for ${batchKey}, flushing immediately`)
      this.flushBatch(batchKey)
      return
    }

    // Clear existing timer
    if (this.batchTimers.has(batchKey)) {
      clearTimeout(this.batchTimers.get(batchKey)!)
    }

    // Set new timer to flush batch
    const timer = setTimeout(() => {
      this.flushBatch(batchKey)
    }, config.debounceMs)

    this.batchTimers.set(batchKey, timer)
  }

  /**
   * Flush a batch queue and save notifications
   */
  private async flushBatch(batchKey: string): Promise<void> {
    const queue = this.batchQueues.get(batchKey)
    if (!queue || queue.length === 0) {
      return
    }

    console.log(`[NotificationAggregator] Flushing batch: ${batchKey}, ${queue.length} notifications`)
    realtimeLogger.logNotificationBatchFlushed(batchKey, queue.length)

    // Clear timer
    if (this.batchTimers.has(batchKey)) {
      clearTimeout(this.batchTimers.get(batchKey)!)
      this.batchTimers.delete(batchKey)
    }

    // Combine similar notifications
    const combined = this.combineNotifications(queue.map(p => p.notification))

    // Save combined notifications
    for (const notification of combined) {
      try {
        await notificationStore.save(notification)
        console.log('[NotificationAggregator] Batched notification saved:', notification.id)
      } catch (error) {
        console.error('[NotificationAggregator] Failed to save batched notification:', error)
        realtimeLogger.logNotificationError(error as Error, { notificationId: notification.id, batchKey })
      }
    }

    // Clear queue
    this.batchQueues.delete(batchKey)

    // Emit updated notifications
    await this.emitNotifications()
  }

  /**
   * Combine similar notifications into fewer notifications
   */
  private combineNotifications(notifications: StoredNotification[]): StoredNotification[] {
    if (notifications.length === 0) {
      return []
    }

    if (notifications.length === 1) {
      return notifications
    }

    const first = notifications[0]
    const { type, metadata } = first

    // Dashboard updates - combine into single update
    if (type === 'dashboard') {
      return [{
        ...first,
        id: `dashboard-${metadata.business_id}-${Date.now()}`,
        title: 'Dashboard Updated',
        message: `Dashboard metrics have been updated (${notifications.length} updates)`,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          updateCount: notifications.length,
          updates: notifications.map(n => n.metadata),
        },
      }]
    }

    // Activity feed - combine into batch notification
    if (type === 'system' && (metadata.event === 'activity:new' || metadata.event === 'activity:update')) {
      return [{
        ...first,
        id: `activity-batch-${metadata.business_id}-${Date.now()}`,
        title: 'New Activities',
        message: `${notifications.length} new activities recorded`,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          activityCount: notifications.length,
          activities: notifications.map(n => n.metadata),
        },
      }]
    }

    // Support ticket updates - combine updates for same ticket
    if (type === 'support-ticket') {
      const ticketId = metadata.support_ticket_id
      
      // Get the most recent status
      const lastNotification = notifications[notifications.length - 1]
      
      return [{
        ...lastNotification,
        id: `support-ticket-${ticketId}-${Date.now()}`,
        title: 'Ticket Updated',
        message: `Support ticket #${ticketId} has ${notifications.length} updates`,
        timestamp: new Date().toISOString(),
        metadata: {
          ...lastNotification.metadata,
          updateCount: notifications.length,
          updates: notifications.map(n => ({
            event: n.metadata.event,
            timestamp: n.timestamp,
          })),
        },
      }]
    }

    // Default: return all notifications unchanged
    return notifications
  }

  /**
   * Save notification to store (with batching support)
   */
  private async saveNotification(notification: StoredNotification): Promise<void> {
    // Check if this notification should be batched
    if (this.shouldBatch(notification)) {
      // Special handling for dashboard updates - enforce 5 second minimum interval
      if (notification.type === 'dashboard') {
        const now = Date.now()
        const timeSinceLastUpdate = now - this.lastDashboardUpdate
        
        if (timeSinceLastUpdate < 5000) {
          console.log(`[NotificationAggregator] Dashboard update throttled (${timeSinceLastUpdate}ms since last)`)
          this.addToBatch(notification)
          return
        }
        
        this.lastDashboardUpdate = now
      }

      this.addToBatch(notification)
      return
    }

    // Save immediately for non-batched notifications
    try {
      await notificationStore.save(notification)
      console.log('[NotificationAggregator] Notification saved:', notification.id)
      await this.emitNotifications()
    } catch (error) {
      console.error('[NotificationAggregator] Failed to save notification:', error)
      realtimeLogger.logNotificationError(error as Error, { notificationId: notification.id })
    }
  }

  /**
   * Load notifications from store and emit to listeners
   */
  private async emitNotifications(): Promise<void> {
    if (!this.userId) return

    try {
      const storedNotifications = await notificationStore.getAll(this.userId, {
        dismissed: false,
      })

      const notifications: Notification[] = storedNotifications.map((stored) => ({
        id: stored.id,
        type: stored.type,
        priority: stored.priority,
        title: stored.title,
        message: stored.message,
        timestamp: stored.timestamp,
        read: stored.read,
        actionUrl: stored.actionUrl,
        metadata: stored.metadata,
      }))

      // Sort by priority then timestamp per requirements
      // Priority order (highest to lowest):
      // 1. Critical: License revocation, payment failures
      // 2. High: License expiring in 1 day, support ticket assigned
      // 3. Normal: License expiring in 3-7 days, ticket status changes
      // 4. Low: Dashboard updates, activity feed
      notifications.sort((a, b) => {
        const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 }
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
        
        if (priorityDiff !== 0) {
          return priorityDiff
        }

        // If same priority, sort by timestamp (newest first)
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      })

      // Emit to all listeners
      const listenerArray = Array.from(this.listeners)
      for (const listener of listenerArray) {
        try {
          listener(notifications)
        } catch (error) {
          console.error('[NotificationAggregator] Listener error:', error)
        }
      }
    } catch (error) {
      console.error('[NotificationAggregator] Failed to emit notifications:', error)
    }
  }

  /**
   * Subscribe to notification updates
   * @param listener - Callback function that receives notification array
   * @returns Unsubscribe function
   */
  subscribe(listener: NotificationListener): () => void {
    this.listeners.add(listener)

    // Immediately emit current notifications to new listener
    this.emitNotifications()

    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      await notificationStore.markAsRead(notificationId)
      console.log('[NotificationAggregator] Notification marked as read:', notificationId)
      await this.emitNotifications()
    } catch (error) {
      console.error('[NotificationAggregator] Failed to mark as read:', error)
      throw error
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    if (!this.userId) {
      console.warn('[NotificationAggregator] Cannot mark all as read: userId not set')
      return
    }

    try {
      await notificationStore.markAllAsRead(this.userId)
      console.log('[NotificationAggregator] All notifications marked as read')
      await this.emitNotifications()
    } catch (error) {
      console.error('[NotificationAggregator] Failed to mark all as read:', error)
      throw error
    }
  }

  /**
   * Dismiss a notification
   */
  async dismiss(notificationId: string): Promise<void> {
    try {
      await notificationStore.dismiss(notificationId)
      console.log('[NotificationAggregator] Notification dismissed:', notificationId)
      await this.emitNotifications()
    } catch (error) {
      console.error('[NotificationAggregator] Failed to dismiss notification:', error)
      throw error
    }
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): number {
    // This is synchronous, so we need to track it separately
    // or make it async. For now, we'll calculate from current notifications
    // This will be updated when we emit notifications
    return 0 // Placeholder - will be calculated by UI from notifications array
  }

  /**
   * Get notifications with optional filtering
   * Returns notifications sorted by priority then timestamp
   */
  async getNotifications(filter?: {
    type?: Notification['type']
    priority?: Notification['priority']
  }): Promise<Notification[]> {
    if (!this.userId) {
      return []
    }

    try {
      const storedNotifications = await notificationStore.getAll(this.userId, {
        dismissed: false,
        type: filter?.type,
        priority: filter?.priority,
      })

      const notifications = storedNotifications.map((stored) => ({
        id: stored.id,
        type: stored.type,
        priority: stored.priority,
        title: stored.title,
        message: stored.message,
        timestamp: stored.timestamp,
        read: stored.read,
        actionUrl: stored.actionUrl,
        metadata: stored.metadata,
      }))

      // Sort by priority then timestamp
      notifications.sort((a, b) => {
        const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 }
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
        
        if (priorityDiff !== 0) {
          return priorityDiff
        }

        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      })

      return notifications
    } catch (error) {
      console.error('[NotificationAggregator] Failed to get notifications:', error)
      return []
    }
  }

  /**
   * Flush all pending batches
   */
  private async flushAllBatches(): Promise<void> {
    console.log('[NotificationAggregator] Flushing all pending batches...')
    
    const batchKeys = Array.from(this.batchQueues.keys())
    
    for (const batchKey of batchKeys) {
      await this.flushBatch(batchKey)
    }
  }

  /**
   * Disconnect all subscriptions and clean up
   */
  async disconnect(): Promise<void> {
    console.log('[NotificationAggregator] Disconnecting...')

    // Flush all pending batches before disconnecting
    await this.flushAllBatches()

    // Clear all batch timers
    this.batchTimers.forEach((timer) => {
      clearTimeout(timer)
    })
    this.batchTimers.clear()
    this.batchQueues.clear()

    // Unsubscribe from all realtime channels
    for (const unsubscribe of this.unsubscribeFunctions) {
      try {
        unsubscribe()
      } catch (error) {
        console.error('[NotificationAggregator] Error unsubscribing:', error)
      }
    }

    this.unsubscribeFunctions = []
    this.listeners.clear()
    this.userId = null
    this.businessId = null
    this.isInitialized = false
    this.lastDashboardUpdate = 0

    console.log('[NotificationAggregator] Disconnected')
  }
}

// Export singleton instance
const notificationAggregator = new NotificationAggregator()

export default notificationAggregator
